/**
 * Загрузка OpenCV.js — обработка снимка документа.
 *
 * **Сборка своя, урезанная.** Готовая весит тринадцать мегабайт: в приложение, которое
 * юрист ставит на домашний экран и открывает в суде без сети, это не кладётся. В нашей
 * оставлены только `core` и `imgproc`, и внутри них — ровно те функции, которые вызывает
 * `scan.ts`. Перечень и команда сборки — `web/scripts/build-opencv.md`.
 *
 * **Подгружается по требованию**, как pdf.js: за обработку снимка платит тот, кто снял
 * документ, а не каждый запуск списка дел ([NFR.md](docs/NFR.md)).
 *
 * Загружается один раз на всё время работы приложения: wasm-модуль инициализируется
 * секунды, и делать это на каждой странице скана значило бы заставлять юриста ждать
 * четырежды подряд.
 */

/** Матрица OpenCV. Освобождается вручную: за wasm-памятью сборщик мусора не следит. */
export interface CvMat {
  rows: number;
  cols: number;
  data32S: Int32Array;
  delete: () => void;
}

export interface CvMatVector {
  size: () => number;
  get: (index: number) => CvMat;
  delete: () => void;
}

export interface CvSize {
  width: number;
  height: number;
}

/**
 * То, чем пользуется `scan.ts`, — не весь OpenCV, а ровно наше подмножество.
 *
 * Описано вручную и коротко: типы `@types/opencv-js` описывают полную сборку, которой
 * у нас нет, и обещали бы функции, отсутствующие в нашем файле.
 */
export interface OpenCv {
  imread: (canvas: HTMLCanvasElement) => CvMat;
  imshow: (canvas: HTMLCanvasElement, mat: CvMat) => void;
  matFromArray: (rows: number, cols: number, type: number, data: number[]) => CvMat;
  cvtColor: (source: CvMat, target: CvMat, code: number) => void;
  GaussianBlur: (source: CvMat, target: CvMat, size: CvSize, sigma: number) => void;
  Canny: (source: CvMat, target: CvMat, low: number, high: number) => void;
  findContours: (
    source: CvMat,
    contours: CvMatVector,
    hierarchy: CvMat,
    mode: number,
    method: number,
  ) => void;
  approxPolyDP: (contour: CvMat, target: CvMat, epsilon: number, closed: boolean) => void;
  arcLength: (contour: CvMat, closed: boolean) => number;
  contourArea: (contour: CvMat) => number;
  isContourConvex: (contour: CvMat) => boolean;
  getPerspectiveTransform: (from: CvMat, to: CvMat) => CvMat;
  warpPerspective: (source: CvMat, target: CvMat, transform: CvMat, size: CvSize) => void;
  adaptiveThreshold: (
    source: CvMat,
    target: CvMat,
    max: number,
    method: number,
    type: number,
    block: number,
    shift: number,
  ) => void;
  Mat: new () => CvMat;
  MatVector: new () => CvMatVector;
  Size: new (width: number, height: number) => CvSize;
  COLOR_RGBA2GRAY: number;
  RETR_LIST: number;
  CHAIN_APPROX_SIMPLE: number;
  CV_32FC2: number;
  ADAPTIVE_THRESH_GAUSSIAN_C: number;
  THRESH_BINARY: number;
}

/** Адрес нашей сборки. Локальный: внешних адресов в приложении нет вовсе. */
const SCRIPT = '/opencv/opencv.js';

/**
 * Модуль до готовности: объект `cv` появляется сразу, а функции в нём — только после
 * инициализации wasm. По наличию `imread` и определяется, дождались ли мы её.
 */
type LoadingOpenCv = Partial<OpenCv> & { onRuntimeInitialized?: () => void };

const readyOrNull = (module: LoadingOpenCv | undefined): OpenCv | null =>
  module && typeof module.imread === 'function' ? (module as OpenCv) : null;

let loading: Promise<OpenCv> | null = null;

/** Сколько ждём инициализации wasm, прежде чем признать, что она не случится. */
const READY_TIMEOUT_MS = 30_000;
const POLL_MS = 30;

/**
 * Ждёт, пока модуль станет пригодным к вызовам.
 *
 * Наша сборка (Emscripten 3.1.64, `MODULARIZE`) кладёт в `window.cv` **промис**,
 * а не модуль: до его разрешения там пустой объект без единой функции. Плюс бывает
 * второй случай — модуль уже есть, но wasm ещё поднимается.
 *
 * Обоих ловит одно место. Через `onRuntimeInitialized` это не решается: обработчик
 * вызывается один раз и только если назначен до инициализации, а мы назначали его
 * в `onload` — и он не вызывался никогда. Экран правки границ при этом просто
 * не открывался, без единой ошибки в консоли. Разбор — PITFALLS §17.
 */
async function whenReady(): Promise<OpenCv> {
  const source = (window as unknown as { cv?: LoadingOpenCv | Promise<LoadingOpenCv> }).cv;
  const resolved = await Promise.resolve(source as LoadingOpenCv | undefined);

  const immediate = readyOrNull(resolved);
  if (immediate) return immediate;

  const deadline = Date.now() + READY_TIMEOUT_MS;
  while (Date.now() < deadline) {
    await new Promise((tick) => window.setTimeout(tick, POLL_MS));

    const ready = readyOrNull(resolved);
    if (ready) return ready;
  }

  throw new Error('OpenCV не поднялся');
}

export function loadOpenCv(): Promise<OpenCv> {
  loading ??= new Promise<OpenCv>((resolve, reject) => {
    // Скрипт мог быть вставлен раньше — тогда ждать нечего, кроме готовности.
    if ((window as unknown as { cv?: unknown }).cv) {
      whenReady().then(resolve, reject);
      return;
    }

    const script = document.createElement('script');
    script.src = SCRIPT;
    script.async = true;
    script.onload = () => whenReady().then(resolve, reject);
    script.onerror = () => reject(new Error('OpenCV не загрузился'));

    document.head.append(script);
  });

  return loading;
}
